import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { Key, Webhook, Bell, RefreshCw, Copy, Eye, EyeOff, Check, Save } from 'lucide-react';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('keys');
  const [keys, setKeys] = useState(null);
  const [webhooks, setWebhooks] = useState({ success_url: '', fail_url: '', webhook_url: '', webhook_secret: '' });
  const [notifications, setNotifications] = useState({ email_on_payment: false, email_on_refund: false, telegram_enabled: false, telegram_chat_id: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => { fetchSettings(); }, [activeTab]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      if (activeTab === 'keys') {
        const res = await settingsAPI.getKeys();
        setKeys(res.data);
      } else if (activeTab === 'webhooks') {
        const res = await settingsAPI.getWebhooks();
        setWebhooks(res.data);
      } else if (activeTab === 'notifications') {
        const res = await settingsAPI.getNotifications();
        setNotifications(res.data);
      }
    } catch (err) {
      console.error('Settings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRegenerateKey = async (type) => {
    if (!window.confirm(`Вы уверены? Старый ${type === 'token' ? 'токен' : 'ключ'} перестанет работать.`)) return;
    try {
      const res = await settingsAPI.regenerateKey(type);
      setKeys(res.data);
    } catch (err) {
      alert('Ошибка генерации ключа');
    }
  };

  const handleSaveWebhooks = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateWebhooks(webhooks);
      alert('Настройки сохранены');
    } catch (err) {
      alert('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateNotifications(notifications);
      alert('Настройки сохранены');
    } catch (err) {
      alert('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'keys', label: 'API ключи', icon: Key },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'notifications', label: 'Уведомления', icon: Bell },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Настройки интеграции</h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-4">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === tab.id ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" /></div>
      ) : (
        <>
          {/* API Keys */}
          {activeTab === 'keys' && keys && (
            <div className="card p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Merchant Token (публичный)</label>
                <div className="flex gap-2">
                  <input type="text" value={keys.merchant_token || ''} readOnly className="input flex-1 font-mono text-sm" />
                  <button onClick={() => copyToClipboard(keys.merchant_token, 'token')} className="btn-secondary">
                    {copied === 'token' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button onClick={() => handleRegenerateKey('token')} className="btn-secondary text-amber-400 hover:bg-amber-500/20">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-slate-500 text-xs mt-1">Используется для идентификации мерчанта в API запросах</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Private Key (секретный)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input type={showPrivateKey ? 'text' : 'password'} value={keys.private_key || ''} readOnly className="input w-full font-mono text-sm pr-10" />
                    <button onClick={() => setShowPrivateKey(!showPrivateKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                      {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button onClick={() => copyToClipboard(keys.private_key, 'private')} className="btn-secondary">
                    {copied === 'private' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button onClick={() => handleRegenerateKey('private')} className="btn-secondary text-amber-400 hover:bg-amber-500/20">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-slate-500 text-xs mt-1">Используется для подписи запросов. Никому не передавайте!</p>
              </div>

              <div className="p-4 bg-slate-700/50 rounded-lg">
                <h4 className="text-white font-medium mb-2">Документация API</h4>
                <p className="text-slate-400 text-sm">Полная документация доступна по адресу: <a href="https://docs.oxypay.kz" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">docs.oxypay.kz</a></p>
              </div>
            </div>
          )}

          {/* Webhooks */}
          {activeTab === 'webhooks' && (
            <div className="card p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Success URL</label>
                <input type="url" value={webhooks.success_url} onChange={e => setWebhooks(w => ({ ...w, success_url: e.target.value }))} className="input w-full" placeholder="https://yoursite.com/payment/success" />
                <p className="text-slate-500 text-xs mt-1">URL для редиректа после успешной оплаты</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Fail URL</label>
                <input type="url" value={webhooks.fail_url} onChange={e => setWebhooks(w => ({ ...w, fail_url: e.target.value }))} className="input w-full" placeholder="https://yoursite.com/payment/fail" />
                <p className="text-slate-500 text-xs mt-1">URL для редиректа при неуспешной оплате</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Webhook URL</label>
                <input type="url" value={webhooks.webhook_url} onChange={e => setWebhooks(w => ({ ...w, webhook_url: e.target.value }))} className="input w-full" placeholder="https://yoursite.com/api/webhook" />
                <p className="text-slate-500 text-xs mt-1">URL для отправки уведомлений о статусе платежа (POST)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Webhook Secret</label>
                <div className="flex gap-2">
                  <input type="text" value={webhooks.webhook_secret} readOnly className="input flex-1 font-mono text-sm" />
                  <button onClick={() => copyToClipboard(webhooks.webhook_secret, 'webhook')} className="btn-secondary">
                    {copied === 'webhook' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-slate-500 text-xs mt-1">Секрет для проверки подписи webhook запросов</p>
              </div>

              <button onClick={handleSaveWebhooks} disabled={saving} className="btn-primary flex items-center gap-2">
                <Save className="w-4 h-4" /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="card p-6 space-y-6">
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={notifications.email_on_payment} onChange={e => setNotifications(n => ({ ...n, email_on_payment: e.target.checked }))} className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-emerald-500" />
                  <span className="text-white">Email при успешном платеже</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={notifications.email_on_refund} onChange={e => setNotifications(n => ({ ...n, email_on_refund: e.target.checked }))} className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-emerald-500" />
                  <span className="text-white">Email при возврате</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={notifications.telegram_enabled} onChange={e => setNotifications(n => ({ ...n, telegram_enabled: e.target.checked }))} className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-emerald-500" />
                  <span className="text-white">Telegram уведомления</span>
                </label>
              </div>

              {notifications.telegram_enabled && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Telegram Chat ID</label>
                  <input type="text" value={notifications.telegram_chat_id} onChange={e => setNotifications(n => ({ ...n, telegram_chat_id: e.target.value }))} className="input w-full" placeholder="123456789" />
                  <p className="text-slate-500 text-xs mt-1">Напишите боту @OxyPayNotifyBot чтобы получить Chat ID</p>
                </div>
              )}

              <button onClick={handleSaveNotifications} disabled={saving} className="btn-primary flex items-center gap-2">
                <Save className="w-4 h-4" /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SettingsPage;
